/**
 * Process Builder Storage Router
 * When USE_GOBODO_API is true, delegates to Gobodo; otherwise uses processBuilderStorage (localStorage).
 * Executions remain in localStorage for both.
 */

import type {
  Process,
  ProcessSummary,
  ProcessBuilderStats,
  SingleProcessStats,
  CreateProcessPayload,
  UpdateProcessPayload,
  CloneProcessPayload
} from '../types/processBuilder';
import { USE_GOBODO_API } from '../utils/gobodoUtils';
import { processBuilderStorage } from './processBuilderStorage';
import * as gobodoService from './gobodo/gobodoProcessBuilderFlowService';

export async function getAllProcessesAsync(): Promise<Process[]> {
  if (USE_GOBODO_API) return gobodoService.getProcesses();
  return Promise.resolve(processBuilderStorage.getAllProcesses());
}

export async function getProcessAsync(processId: string): Promise<Process | null> {
  if (USE_GOBODO_API) return gobodoService.getProcessById(processId);
  return Promise.resolve(processBuilderStorage.getProcess(processId));
}

export async function getProcessSummariesAsync(): Promise<ProcessSummary[]> {
  if (USE_GOBODO_API) return gobodoService.getProcessSummaries();
  return Promise.resolve(processBuilderStorage.getProcessSummaries());
}

export async function getStatsAsync(): Promise<ProcessBuilderStats> {
  if (USE_GOBODO_API) return gobodoService.getStats();
  return Promise.resolve(processBuilderStorage.getStats());
}

export async function getProcessStatsAsync(processId: string): Promise<SingleProcessStats | null> {
  if (USE_GOBODO_API) {
    const process = await gobodoService.getProcessById(processId);
    if (!process) return null;
    const executions = processBuilderStorage.getExecutionsForProcess(processId, 1000);
    const successCount = executions.filter(e => e.status === 'completed').length;
    const failureCount = executions.filter(e => e.status === 'failed').length;
    return {
      processId: process.id,
      processName: process.name,
      totalExecutions: executions.length,
      successCount,
      failureCount,
      successRate: executions.length > 0 ? Math.round((successCount / executions.length) * 100) : 0,
      avgExecutionTime: 0,
      lastExecuted: executions[0]?.startTime
    };
  }
  return Promise.resolve(processBuilderStorage.getProcessStats(processId));
}

export async function createProcessAsync(payload: CreateProcessPayload): Promise<Process> {
  if (USE_GOBODO_API) return gobodoService.createProcess(payload);
  return Promise.resolve(processBuilderStorage.createProcess(payload));
}

export async function updateProcessAsync(payload: UpdateProcessPayload): Promise<Process | null> {
  if (USE_GOBODO_API) return gobodoService.updateProcess(payload);
  return Promise.resolve(processBuilderStorage.updateProcess(payload));
}

export async function deleteProcessAsync(processId: string): Promise<boolean> {
  if (USE_GOBODO_API) return gobodoService.deleteProcess(processId);
  return Promise.resolve(processBuilderStorage.deleteProcess(processId));
}

export async function cloneProcessAsync(payload: CloneProcessPayload): Promise<Process | null> {
  if (USE_GOBODO_API) return gobodoService.cloneProcess(payload);
  return Promise.resolve(processBuilderStorage.cloneProcess(payload));
}

export async function activateProcessAsync(processId: string): Promise<Process | null> {
  if (USE_GOBODO_API) return gobodoService.activateProcess(processId);
  return Promise.resolve(processBuilderStorage.activateProcess(processId));
}

export async function deactivateProcessAsync(processId: string): Promise<Process | null> {
  if (USE_GOBODO_API) return gobodoService.deactivateProcess(processId);
  return Promise.resolve(processBuilderStorage.deactivateProcess(processId));
}
